import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../config/api';
import { subscribe, publish } from '../../utils/eventBus';
import { APP_VERSION, APP_BUILD_DATE, CHANGELOG } from '../../config/version';

export default function ProfileSettings({ navigate, isLoggedIn }) {
  const [form, setForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const statusClass = (status) => {
    const value = (status || '').toLowerCase();
    if (['delivered'].includes(value)) return 'text-green-600';
    if (['processing', 'confirmed', 'paid'].includes(value)) return 'text-blue-600';
    if (['shipped', 'out_for_delivery', 'out for delivery'].includes(value)) return 'text-indigo-600';
    if (['cancelled', 'failed'].includes(value)) return 'text-red-600';
    return 'text-gray-600';
  };

  const fetchProfile = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.user) {
          const u = data.user;
          setForm({
            username: u.username || '',
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            email: u.email || '',
            mobile: u.mobile || '',
            addressLine1: u.address?.line1 || '',
            addressLine2: u.address?.line2 || '',
            city: u.address?.city || '',
            state: u.address?.state || '',
            postalCode: u.address?.postalCode || '',
          });
        }
      })
      .catch((err) => console.error('Fetch profile error', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchProfile();

    // subscribe to login events so we re-fetch if login happens elsewhere
    const unsub = subscribe('auth:login', () => {
      fetchProfile();
    });
    return () => unsub();
  }, [isLoggedIn, fetchProfile]);

  useEffect(() => {
    // fetch orders for authenticated user
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.orders) setOrders(data.orders);
        else if (res.status === 401) {
          // token expired or invalid — log out locally
          localStorage.removeItem('authToken');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userName');
          publish('auth:logout');
        }
      } catch (e) {
        console.error('Fetch orders error', e);
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [form.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setMessage('Please login to save your profile');
      return;
    }

    const payload = {
      username: form.username || undefined,
      firstName: form.firstName,
      lastName: form.lastName,
      mobile: form.mobile,
      address: {
        line1: form.addressLine1,
        line2: form.addressLine2,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
      },
    };

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data.message || 'Failed to update profile');
      } else {
        setMessage('Profile updated');
      }
    } catch (err) {
      console.error('Update profile error', err);
      setMessage('Unable to save profile');
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="container mx-auto p-4 md:p-8">
        <div className="max-w-md mx-auto bg-white rounded-xl shadow p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Please login to view your profile</h2>
          <p className="text-sm text-gray-500 mb-4">You need to be logged in to see and edit your profile details.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('login')} className="px-4 py-2 bg-blue-600 text-white rounded-md">Login</button>
            <button onClick={() => navigate('register')} className="px-4 py-2 border rounded-md">Register</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-8 pb-28">
      <div className="max-w-xl mx-auto space-y-6">
        <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600 p-5 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
              <img src={`https://placehold.co/120x120/DDD/000?text=U`} alt="avatar" className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{form.firstName || form.lastName ? `${form.firstName} ${form.lastName}`.trim() : 'Your Profile'}</h2>
              {form.username ? (
                <div className="text-sm text-white/80">@{form.username}</div>
              ) : (
                <div className="text-xs text-yellow-200">Set a username in your profile</div>
              )}
              <div className="text-xs text-white/70 mt-1">{form.email}</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-lg p-5 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Personal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input name="username" value={form.username} readOnly placeholder="Username" className="w-full p-3 rounded-xl border border-gray-200 bg-gray-50" />
              <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="First name" className="w-full p-3 rounded-xl border border-gray-200" />
              <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Last name" className="w-full p-3 rounded-xl border border-gray-200" />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Contact</h3>
            <div className="grid grid-cols-1 gap-3">
              <input name="email" value={form.email} readOnly placeholder="E-mail" className="w-full p-3 rounded-xl border border-gray-200 bg-gray-50" />
              <input name="mobile" value={form.mobile} onChange={handleChange} placeholder="Mobile" className="w-full p-3 rounded-xl border border-gray-200" />
            </div>
            <div className="text-xs text-gray-500 mt-1">Registered email: {form.email}</div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Address</h3>
            <div className="grid grid-cols-1 gap-3">
              <input name="addressLine1" value={form.addressLine1} onChange={handleChange} placeholder="Address line 1" className="w-full p-3 rounded-xl border border-gray-200" />
              <input name="addressLine2" value={form.addressLine2} onChange={handleChange} placeholder="Address line 2" className="w-full p-3 rounded-xl border border-gray-200" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input name="city" value={form.city} onChange={handleChange} placeholder="City" className="w-full p-3 rounded-xl border border-gray-200" />
                <input name="state" value={form.state} onChange={handleChange} placeholder="State" className="w-full p-3 rounded-xl border border-gray-200" />
                <input name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="Postal code" className="w-full p-3 rounded-xl border border-gray-200" />
              </div>
            </div>
          </div>

          {message && <p className="text-sm text-center text-green-600">{message}</p>}

          <div className="pt-2">
            <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-full font-semibold shadow-md hover:bg-blue-700">
              {loading ? 'Saving...' : 'SAVE'}
            </button>
          </div>
        </form>

        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-3">
          <h3 className="text-lg font-semibold">Support</h3>
          <button onClick={() => {
            // clear auth and notify app
            localStorage.removeItem('authToken');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userName');
            publish('auth:logout');
            navigate('products');
          }} className="w-full text-left py-2 px-3 rounded-md hover:bg-gray-50 text-red-600 font-medium">🔓 Logout</button>

          <button onClick={() => navigate('support')} className="w-full text-left py-2 px-3 rounded-md hover:bg-gray-50">❓ Help & Support</button>
          <a href="tel:+0000000000" className="block w-full text-left py-2 px-3 rounded-md hover:bg-gray-50">📞 Contact Us</a>
          <a href="/terms" className="block w-full text-left py-2 px-3 rounded-md hover:bg-gray-50">📜 Terms & Privacy</a>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-5">
          <h3 className="text-lg font-semibold mb-3">Order History</h3>
          {ordersLoading ? (
            <div>Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-sm text-gray-600">No orders yet.</div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o._id} className="flex items-center justify-between p-3 rounded-md border">
                  <div>
                    <div className="text-sm font-medium">Order #{o.orderNumber || o._id}</div>
                    <div className="text-xs text-gray-500">{new Date(o.createdAt).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${statusClass(o.status)}`}>{o.status}</div>
                    <button onClick={() => navigate('order', o._id)} className="text-xs text-blue-600 mt-1">View</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* App Version & Changelog */}
        <div className="bg-white rounded-2xl shadow-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">App Info</h3>
            <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full">
              v{APP_VERSION}
            </span>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Version</span>
              <span className="font-medium text-gray-900">{APP_VERSION}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Build Date</span>
              <span className="font-medium text-gray-900">{APP_BUILD_DATE}</span>
            </div>
            
            {/* Latest Changes */}
            {CHANGELOG.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm font-semibold text-gray-700">What's New</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    CHANGELOG[0].type === 'major' ? 'bg-red-100 text-red-700' :
                    CHANGELOG[0].type === 'minor' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {CHANGELOG[0].type}
                  </span>
                </div>
                <ul className="text-xs text-gray-600 space-y-1.5 max-h-32 overflow-y-auto">
                  {CHANGELOG[0].changes.slice(0, 5).map((change, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {change}
                    </li>
                  ))}
                  {CHANGELOG[0].changes.length > 5 && (
                    <li className="text-purple-600 font-medium">
                      +{CHANGELOG[0].changes.length - 5} more changes
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
